import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Detección de inactividad real del usuario.
 *
 * El problema que resuelve: el SPA renueva el token cada 30 s de forma
 * incondicional, lo que mantiene viva la sesión en Keycloak y hace que su
 * `ssoSessionIdleTimeout` **no salte nunca**. Con una pestaña abierta, la
 * sesión sobrevivía hasta el máximo absoluto. Endurecer Keycloak sin esto no
 * cambiaría nada.
 *
 * Aquí se mide la actividad REAL (eventos del DOM). El proveedor de auth usa
 * `shouldRefresh` para dejar de renovar cuando el usuario está inactivo, de
 * modo que el temporizador de la app y el del IdP convergen en vez de pelearse.
 *
 * No hace falta sincronizar entre pestañas: los tokens viven en
 * `sessionStorage`, que ya es por pestaña.
 */

export const IDLE_LIMIT_MS = 30 * 60 * 1000; // 30 min (igual que ssoSessionIdleTimeout)
export const WARNING_BEFORE_MS = 2 * 60 * 1000; // avisar 2 min antes
const ACTIVITY_THROTTLE_MS = 1000;
const CHECK_INTERVAL_MS = 5000;

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "click",
  "scroll",
  "touchstart",
] as const;

interface UseIdleTimeoutOptions {
  enabled: boolean;
  onTimeout: () => void;
  idleLimitMs?: number;
  warningBeforeMs?: number;
}

interface UseIdleTimeoutResult {
  /** True cuando queda poco para cerrar sesión (se muestra el aviso). */
  showWarning: boolean;
  /** Segundos restantes hasta el cierre automático. */
  secondsLeft: number;
  /** False mientras el usuario está inactivo: el proveedor deja de renovar. */
  shouldRefresh: () => boolean;
  /** Marca actividad manualmente ("Seguir conectado"). */
  keepAlive: () => void;
}

export function useIdleTimeout({
  enabled,
  onTimeout,
  idleLimitMs = IDLE_LIMIT_MS,
  warningBeforeMs = WARNING_BEFORE_MS,
}: UseIdleTimeoutOptions): UseIdleTimeoutResult {
  // En un ref, no en estado: la actividad del ratón no debe re-renderizar.
  const lastActivityRef = useRef<number>(Date.now());
  const lastThrottleRef = useRef<number>(0);
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(warningBeforeMs / 1000));

  const markActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastThrottleRef.current < ACTIVITY_THROTTLE_MS) return;
    lastThrottleRef.current = now;
    lastActivityRef.current = now;
  }, []);

  const keepAlive = useCallback(() => {
    lastActivityRef.current = Date.now();
    lastThrottleRef.current = 0;
    setShowWarning(false);
  }, []);

  // `shouldRefresh` es lo que corta la renovación automática: mientras el
  // aviso está en pantalla dejamos de tocar la sesión en Keycloak.
  const shouldRefresh = useCallback(() => {
    const idleFor = Date.now() - lastActivityRef.current;
    return idleFor < idleLimitMs - warningBeforeMs;
  }, [idleLimitMs, warningBeforeMs]);

  useEffect(() => {
    if (!enabled) {
      setShowWarning(false);
      return;
    }

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, markActivity, { passive: true })
    );

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const idleFor = Date.now() - lastActivityRef.current;
        // Si la pestaña estuvo en segundo plano más del límite, cerramos sin
        // enseñar una cuenta atrás: decir "te quedan 1:59" tras horas fuera
        // sería mentir.
        if (idleFor >= idleLimitMs) {
          onTimeout();
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const interval = window.setInterval(() => {
      const idleFor = Date.now() - lastActivityRef.current;

      if (idleFor >= idleLimitMs) {
        setShowWarning(false);
        onTimeout();
        return;
      }

      if (idleFor >= idleLimitMs - warningBeforeMs) {
        setShowWarning(true);
        setSecondsLeft(Math.max(0, Math.ceil((idleLimitMs - idleFor) / 1000)));
      } else {
        setShowWarning(false);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, markActivity));
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(interval);
    };
  }, [enabled, idleLimitMs, warningBeforeMs, markActivity, onTimeout]);

  return { showWarning, secondsLeft, shouldRefresh, keepAlive };
}
